import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { switchMap, publishReplay, refCount, catchError, map } from 'rxjs/operators';
import { DATicketStatus, ErrorLevel, ErrorType, TicketStatus } from 'src/app/models/enums';
import { ISellBetRequestTm } from 'src/app/models/jacktime/transportObjects/sellBetRequestTmTO.interface';
import { ICustomNotification } from 'src/app/models/notification/customNotification.interface';
import { IPlayedTicket } from 'src/app/models/ticket/playedTicket';
import { IPlayedTicketState } from 'src/app/models/ticket/playedTicketState.interface';
import { LoaderService } from 'src/app/services/loader.service';
import { NotificationService } from 'src/app/services/notification.service';
import { TicketProviderService } from 'src/app/services/providers/ticket.provider.service';
import { SettingsService } from 'src/app/services/settings.service';
import { ModelMapperService } from 'src/app/services/util/model.mapper.service';
import { DynamicTranslations } from 'src/locale/dynamicTranslations';
import { ICommissionEstimateReport } from '../models/commissions/commissionEstimateReport.interface';
import { ICommissionReport } from '../models/commissions/commissionReport.interface';
import { ISalesReportsByAffiliate } from '../models/salesReport/salesReportsByAffiliate.interface';
import { IAffiliatePlayedTicket } from '../models/ticket/affiliatePlayedTicket.interface';
import { IAffiliate } from '../models/user/affiliate.interface';
import { AffiliateRole } from '../models/util/tmEnums';
import { AuthenticationService } from './authentication.service';
import { ModelMapperTMService } from './model-mapper-tm.service';
import { TicketManagerTicketProviderService } from './providers/ticket-manager-ticket-provider.service';
import { IShopReport } from '../models/shopReport/shopReport.interface';
import { IPaidReportsByAffiliate } from '../models/paidReport/paidReportsByAffiliate.interface';

@Injectable({
  providedIn: 'root'
})
export class TicketManagerTicketService {
  private ticketStatusSource = new Subject<{ticketNumber: string; status: DATicketStatus; amount: number}>();
  public ticketStatusChanged$ = this.ticketStatusSource.asObservable();

  constructor(
    private authService: AuthenticationService,
    private modelMapperTM: ModelMapperTMService,
    private ticketProviderTM: TicketManagerTicketProviderService,
    private settingsService: SettingsService,
    private ticketProvider: TicketProviderService,
    private modelMapper: ModelMapperService,
    private notificationService: NotificationService,
    private loaderService: LoaderService) {
  }

  getTicketsAFF(shopIds: number[], affiliateIds: number[], dateFrom: Date, dateTo: Date, affiliates: IAffiliate[]): Observable<IAffiliatePlayedTicket[]> {
    this.loaderService.showHideLoader(true);
    return this.ticketProviderTM.getTicketsAFF(this.authService.loggedAffiliate.affiliateRole === AffiliateRole.agentManager ?  this.authService.loggedAffiliate.parentId : this.authService.loggedAffiliate.affiliateId, affiliateIds?.length > 0 ? null : shopIds, affiliateIds, dateFrom, dateTo).pipe(
      switchMap((tickets) => {
        this.loaderService.showHideLoader(false);
        if (tickets) {
          return of(tickets.map(ticket => {
            let cashier: IAffiliate = null;
            let agent: IAffiliate = null;
            if (this.authService.loggedAffiliate.affiliateRole === AffiliateRole.cashier) {
              cashier = this.authService.loggedAffiliate;
            } else {
              cashier = affiliates.find(aff => aff.affiliateId === ticket.affiliateId);
              agent = this.authService.loggedAffiliate.affiliateId  === cashier?.parentId ? this.authService.loggedAffiliate : affiliates.find(aff => aff.affiliateId === cashier?.parentId);
            }
            return this.modelMapperTM.mapPlayedTicket(ticket, agent ? agent.username : '', cashier ? cashier.username : '');
          }));
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        this.loaderService.showHideLoader(false);
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }


  getCommissionReport(dateFrom: Date, dateTo: Date): Observable<ICommissionReport[]> {
    this.loaderService.showHideLoader(true);
    return this.ticketProviderTM.getCommissionReport(this.authService.loggedAffiliate.affiliateRole === AffiliateRole.agentManager ?  this.authService.loggedAffiliate.parentId : this.authService.loggedAffiliate.affiliateId, dateFrom, dateTo).pipe(
      switchMap((reports) => {
        this.loaderService.showHideLoader(false);
        if (reports) {
          const reporsRet = Object.values(reports);
          return of(reporsRet.map(report => this.modelMapperTM.mapCommissionReport(report)));
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        this.loaderService.showHideLoader(false);
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  // to be removed and also method on provider
  // getBetTickets(affiliateId: number, limit: number, offset: number, dateFrom: Date, dateTo: Date, filter: string): Observable<IPlayedTicket[]> {
  //   return this.settingsService.getBetTypeShowTypes().pipe(
  //     switchMap(betTypeShowTypes =>
  //       this.ticketProvider.getTicketsForMobileAffiliate(affiliateId, limit, offset, dateFrom, dateTo, filter)
  //         .pipe(
  //           map(playedTickets =>
  //             playedTickets.map(playedTicket => this.modelMapper.mapPlayedTicket(playedTicket, betTypeShowTypes, affiliateId)
  //             )
  //           )
  //         )
  //     ));
  // }

  getTicketsByFilterAndPaging(affiliateId: number, limit: number, offset: number, dateFrom: Date, dateTo: Date, filter: string[], affiliates: IAffiliate[]): Observable<{ totalCount: number; tickets: IAffiliatePlayedTicket[] }> {
    this.loaderService.showHideLoader(true);
    return this.ticketProviderTM.getTicketsByFilterAndPaging(affiliateId, limit, offset, dateFrom, dateTo, filter)
      .pipe(
        map(playedTicketsWithCount => {
          this.loaderService.showHideLoader(false);
          return ({
            totalCount: playedTicketsWithCount.totalCount, tickets: playedTicketsWithCount.tickets.map(playedTicket => {
              let cashier: IAffiliate = null;
              let agent: IAffiliate = null;
              if (this.authService.loggedAffiliate.affiliateRole === AffiliateRole.cashier) {
                cashier = this.authService.loggedAffiliate;
              } else {
                cashier = affiliates.find(aff => aff.affiliateId === playedTicket.affiliateId);
                agent = this.authService.loggedAffiliate.affiliateId === cashier?.parentId ? this.authService.loggedAffiliate : affiliates.find(aff => aff.affiliateId === cashier?.parentId);
              }

              return this.modelMapperTM.mapPlayedTicket(playedTicket, agent ? agent.username : '', cashier ? cashier.username : '');
            })
          });
        })
      );
  }

  getCommissionReportEstimate(dateFrom: Date, dateTo: Date): Observable<ICommissionEstimateReport[]> {
    this.loaderService.showHideLoader(true);
    return this.ticketProviderTM.getCommissionReportEstimate(this.authService.loggedAffiliate.affiliateRole === AffiliateRole.agentManager ?  this.authService.loggedAffiliate.parentId : this.authService.loggedAffiliate.affiliateId, dateFrom, dateTo).pipe(
      switchMap((reports) => {
        this.loaderService.showHideLoader(false);
        if (reports) {
          const reporsRet = Object.values(reports);
          return of(reporsRet.map(report => this.modelMapperTM.mapCommissionEstimateReport(report)));
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        this.loaderService.showHideLoader(false);
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  getExternalTicketByTicketNumber(ticketNumber: string, playedById: number, betTicketStatusMapped?: IPlayedTicketState): Observable<IPlayedTicket> {
    return this.settingsService.getBetTypeShowTypes().pipe(
      switchMap(betTypeShowTypes =>
        this.ticketProviderTM.getExternalTicketByTicketNumber(ticketNumber).pipe(
          map(playedTicket => {
            if (!playedTicket) {
              return null;
            }
            const pt = this.modelMapper.mapPlayedTicket(playedTicket, betTypeShowTypes, playedById);
            if (betTicketStatusMapped) {
              pt.status = betTicketStatusMapped;
            }
            return pt;
          })
        )
      )
    );
  }

  changeCashOutTicketStatus(ticketNumber: string, amount: number) {
    this.ticketStatusSource.next({ticketNumber, status: DATicketStatus.CashOut, amount});
  }

  stornoTicket(ticketNumber: string, pinCode: string): Observable<boolean> {
    this.loaderService.showHideLoader(true);
    return this.ticketProviderTM.stornoTicket(ticketNumber, pinCode).pipe(
      switchMap((isSuccess) => {
        if (!isSuccess) {
          this.loaderService.showHideLoader(false);
          return of(false);
        }

        this.ticketStatusSource.next({ticketNumber, status: DATicketStatus.Storno, amount: 0});
        const notification = {
          title: DynamicTranslations.notification_title,
          message: DynamicTranslations.ticketManager_TicketStorned,
          errorLevel: ErrorLevel.Success,
          errorType: ErrorType.CloseDialog,
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        this.loaderService.showHideLoader(false);
        return of(true);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        this.loaderService.showHideLoader(false);
        // observableThrowError(this.handleError(err));
        console.log(err);
        return of(false);
      }),
    );
  }

  payoutTicket(ticketNumber: string, pinCode: string, currentStatus: TicketStatus): Observable<boolean> {
    this.loaderService.showHideLoader(true);
    return this.ticketProviderTM.payoutTicket(ticketNumber, pinCode).pipe(
      switchMap((isSuccess) => {
        this.loaderService.showHideLoader(false);
        if (!isSuccess) {
          return of(false);
        }
        this.ticketStatusSource.next({
          ticketNumber,
          status: currentStatus === TicketStatus.Void ? DATicketStatus.VoidPaid : (
            currentStatus === TicketStatus.CashBack ? DATicketStatus.CashBackPaid : DATicketStatus.Paid
          ),
          amount: null
        });
        const notification = {
          title: DynamicTranslations.notification_title,
          message: DynamicTranslations.ticketManager_TicketPaidout,
          errorLevel: ErrorLevel.Success,
          errorType: ErrorType.CloseDialog,
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        return of(true);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        this.loaderService.showHideLoader(false);
        // observableThrowError(this.handleError(err));
        console.log(err);
        return of(false);
      }),
    );
  }

  getExternalTicketWithPlatformTicketStatus(ticketNumber: string, pinCode: string): Observable<IPlayedTicket> {
    return this.settingsService.getBetTypeShowTypes().pipe(
      switchMap(betTypeShowTypes =>
        this.ticketProviderTM.getExternalAndPlatformTicket(ticketNumber, pinCode).pipe(
          switchMap((platformAndBettingTicket) => {
            if (!platformAndBettingTicket) {
              return of(null);
            }
            const affiliatePlayedTicket = this.modelMapperTM.mapPlayedTicket(platformAndBettingTicket.platformTicket);
            const playedTicket: IPlayedTicket = this.modelMapper.mapPlayedTicket(platformAndBettingTicket.bettingTicket, betTypeShowTypes, affiliatePlayedTicket.affiliateId);
            playedTicket.status = affiliatePlayedTicket.betTicketStatus;
            return of(playedTicket);
          }),
          publishReplay(1),
          refCount(),
          catchError((err) => {
            // observableThrowError(this.handleError(err));
            console.log(err);
            return of(null);
          })
        )
      )
    );
  }

  getSalesReport(affiliateIds: number[], dateFrom: Date, dateTo: Date): Observable<ISalesReportsByAffiliate[]> {
    this.loaderService.showHideLoader(true);
    return this.ticketProviderTM.getSalesReport(affiliateIds, dateFrom, dateTo).pipe(
      switchMap((reports) => {
        this.loaderService.showHideLoader(false);
        if (reports) {
          return of(reports.map(report => this.modelMapperTM.mapSalesReportsByAffiliate(report)));
        }
        return of([]);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        this.loaderService.showHideLoader(false);
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  getPaidReport(affiliateIds: number[], dateFrom: Date, dateTo: Date): Observable<IPaidReportsByAffiliate[]> {
    this.loaderService.showHideLoader(true);
    return this.ticketProviderTM.getPaidReport(affiliateIds, dateFrom, dateTo).pipe(
      switchMap((reports) => {
        this.loaderService.showHideLoader(false);
        if (reports) {
          return of(reports.map(report => this.modelMapperTM.mapPaidReportsByAffiliate(report)));
        }
        return of([]);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        this.loaderService.showHideLoader(false);
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  getShopReport(affiliateId: number, dateFrom: Date, dateTo: Date): Observable<IShopReport> {
    this.loaderService.showHideLoader(true);
    return this.ticketProviderTM.getShopReport(affiliateId, dateFrom, dateTo).pipe(
      switchMap((report) => {
        this.loaderService.showHideLoader(false);
        if (report) {
          return of(this.modelMapperTM.mapShopReport(report));
        }
        return of();
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        this.loaderService.showHideLoader(false);
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

}
